import React, { useState, useEffect, useMemo, useCallback } from "react";
import ReactECharts from "echarts-for-react";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import "./Production.css";

import { useSimulationData } from "../../../../hooks/useSimulationData";
import productionWorkerService from "../../../../services/productionWorkerService";
import { useExportedData } from "../../../../contexts/ExportedDataContext";
import { parseSimulationDate } from "../../utils/simulationDate";
import {
  buildDataZoom,
  HOURLY_BUCKET_STEP,
  darkTooltip,
  formatDate,
  getXAxisTitle,
} from "../../utils/chartOptions";
import {
  getDeviceColor,
  SOLAR_BASE_COLOR,
  GENERATOR_BASE_COLOR,
} from "../../utils/deviceColors";

const PRODUCTION_TOTAL_SERIES_NAME = "Production Total";

export default function ProductionChart({
  csvData,
  proddeviceNames,
  onLoadingStateChange,
}) {
  /* ── data from CSV ─────────────────────── */
  const { dailyData, isLoading } = useSimulationData(csvData);

 

  /* ── exported data context ─────────────── */
  const { setExportedProductionData } = useExportedData();
  /* ── UI state ──────────────────────────── */
  const [view, setView] = useState("yearly");
  const [selDate, setSelDate] = useState("");
  const [selMonth, setSelMonth] = useState("");
  const [selYear, setSelYear] = useState("");
  const [chartData, setChartData] = useState([]);
  const formatSelectedPeriodType = () => {
    if (view === "daily") {
      return "Day";
    } else if (view === "monthly") {
      return "Month";
    } else if (view === "yearly") {
      return "Year";
    } else if (view === "all time view") {
      return "All Time View";
    }
    return "";
  };
  const formatSelectedPeriod = () => {
    if (view === "daily") {
      const [year, month, day] = selDate.split("-");
      return `${month}/${day}/${year}`; // MM/DD/YYYY
    } else if (view === "monthly") {
      const [year, month] = selMonth.split("-");
      const monthName = new Date(year, month - 1).toLocaleString("default", {
        month: "long",
      });
      return `${monthName} ${year}`;
    } else if (view === "yearly") {
      return selYear;
    } else if (view === "all time view") {
      return "";
    }
    return "";
  };

  /* ── capacity lookup once devices arrive ─ */

  /* The Date column reaches here as whatever string the result file carried —
   * "01/01/2040 00:00" from the sim, ISO from other writers — so every period
   * key below comes from a parse rather than a fixed offset into the stamp.
   * "01/01/2040".slice(0, 4) is "01/0", which matches Jan 1-9 of any year: a
   * yearly view built that way both truncates and cross-contaminates years.
   * One parse per row, positionally aligned with dailyData. */
  const periodKeys = useMemo(
    () => dailyData.map((row) => parseSimulationDate(row?.Date)),
    [dailyData],
  );

  /* First and last stamp the result actually covers. The native pickers below
   * reject a min/max they cannot read as ISO, which these keys always are. */
  const [firstKey, lastKey] = useMemo(() => {
    const readable = periodKeys.filter(Boolean);
    return [readable[0] ?? null, readable[readable.length - 1] ?? null];
  }, [periodKeys]);

  const availableYears = useMemo(
    () => Array.from(new Set(periodKeys.filter(Boolean).map((key) => key.year))),
    [periodKeys],
  );

  /* ── initialise selectors once ─────────── */
  useEffect(() => {
    if (!firstKey) return;
    setSelDate(firstKey.day);
    setSelMonth(firstKey.month);
    setSelYear(firstKey.year);
  }, [firstKey]);

  /* Falls back to the first period the result covers rather than slicing on an
   * empty selector. The selectors are filled by an effect, so for one render
   * after a view switch the new view's selector is still "". The offset-slicing
   * version this replaced papered over that case only for the daily view, and
   * only by accident: `Date.startsWith("")` is true of every row, so switching
   * to Daily briefly plotted the WHOLE dataset under a one-day label, while
   * switching to Monthly or Yearly plotted nothing. Slicing on the first period
   * instead shows the right rows immediately in all three, and keeps the chart
   * mounted - an empty chartData unmounts ReactECharts, which disposes the
   * instance and takes the user's zoom range and legend selection with it. */
  const activeDay = selDate || firstKey?.day || "";
  const activeMonth = selMonth || firstKey?.month || "";
  const activeYear = selYear || firstKey?.year || "";

  /* ── slice rows whenever view changes ──── */
  useEffect(() => {
    if (!dailyData.length) return;
    let rows = [];
    if (view === "daily")
      rows = dailyData.filter((_, i) => periodKeys[i]?.day === activeDay);
    if (view === "monthly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.month === activeMonth);
    if (view === "yearly")
      rows = dailyData.filter((_, i) => periodKeys[i]?.year === activeYear);
    if (view === "all time view") rows = dailyData;
    setChartData(rows);
  }, [view, activeDay, activeMonth, activeYear, dailyData, periodKeys]);

  const xAxisTitle = getXAxisTitle(view);

  /* ── READY? call the worker ────────────── */
  const hasDevices =
    (proddeviceNames?.Solar?.length ?? 0) + (proddeviceNames?.Generator?.length ?? 0) >
    0;
 
  useEffect(() => {
    if (!chartData.length || !hasDevices) return; // ← wait until both ready

    productionWorkerService
      .run({
        type: "CALCULATE_PRODUCTION_PER_DEVICE",
        data: { chartData, proddeviceNames },
      })
      .then((list) => {
        const stamped = list.map((m) => ({
          ...m,
          view: formatSelectedPeriodType(),
          selectedPeriod: formatSelectedPeriod(), // default if none match
          /* The period the card is describing, as ISO KEYS rather than the
           * display strings beside them.
           *
           * "January 2040" is a label; a card that has to slice the daily
           * summary by the same period cannot use it without reverse-parsing a
           * locale string back into "2040-01", which is a guess that changes
           * with the browser's locale. These are the exact keys
           * `sumDemandChargeForPeriod` and `sumDeviceAttributionForPeriod`
           * filter on -- startsWith / slice(0,7) / slice(0,4) -- and
           * `periodView` is the raw view token, not the "Day"/"Month"/"Year"
           * label above it.
           *
           * Stamped from the ACTIVE keys, not the selector state: the selectors
           * are filled by an effect, so for one render after a view switch the
           * new view's selector is still "" while the rows below were already
           * sliced by the fallback. Stamping the selector would label the card
           * with a period the figures on it do not cover. */
          periodView: view,
          selDate: activeDay,
          selMonth: activeMonth,
          selYear: activeYear,
        }));
        setExportedProductionData(stamped);
      
        onLoadingStateChange?.(false);
      })
      .catch(console.error);
  }, [
    chartData,
    hasDevices,
    view,
    selDate,
    selMonth,
    selYear,
    activeDay,
    activeMonth,
    activeYear,
    proddeviceNames,
  ]);

  /* ── notify parent while loading ───────── */
  useEffect(() => {
    onLoadingStateChange?.(isLoading || !chartData.length || !hasDevices);
  }, [isLoading, chartData.length, hasDevices]);

  /* ── very small chart just to keep UI alive ─ */
  const option = useMemo(
    () => ({
      xAxis: { type: "category", data: chartData.map((r) => r.Date) },
      yAxis: { type: "value" },
      series: [{ type: "line", data: chartData.map(() => 0) }],
    }),
    [chartData],
  );
  

  const echartOptions = useMemo(
    () => {
      const solarSeries = Array.isArray(proddeviceNames?.Solar)
        ? proddeviceNames.Solar.map((name, index) => ({
            name,
            type: "line",
            data: chartData.map(
              (entry) => Number(entry[`${name} Solar (kWh)`]) || 0,
            ),
            itemStyle: { color: getDeviceColor(SOLAR_BASE_COLOR, index) },
            symbol: "circle",
            symbolSize: 4,
            lineStyle: { width: 2 },
          }))
        : [];

      const generatorSeries = Array.isArray(proddeviceNames?.Generator)
        ? proddeviceNames.Generator.map((name, index) => ({
            name,
            type: "line",
            step: HOURLY_BUCKET_STEP,
            data: chartData.map(
              (entry) => Number(entry[`${name} Generator Output (kWh)`]) || 0,
            ),
            itemStyle: { color: getDeviceColor(GENERATOR_BASE_COLOR, index) },
            symbol: "circle",
            symbolSize: 4,
            lineStyle: { width: 2 },
          }))
        : [];

      const productionDeviceSeries = [...solarSeries, ...generatorSeries];
      const productionTotalSeries =
        productionDeviceSeries.length > 0
          ? chartData.map((_, index) =>
              productionDeviceSeries.reduce(
                (sum, series) => sum + (Number(series.data[index]) || 0),
                0,
              ),
            )
          : [];

      return {
        animation: true,
        animationDuration: 1000,
        animationEasing: "cubicInOut",
        tooltip: darkTooltip(),
        legend: {
          show: true,
          data: [
            ...(productionDeviceSeries.length > 0
              ? [PRODUCTION_TOTAL_SERIES_NAME]
              : []),
            ...(proddeviceNames?.Solar || []),
            ...(proddeviceNames?.Generator || []),
          ],
          top: 0,
          right: 10,
          textStyle: {
            color: "#666",
            fontSize: 10,
          },
        },
        dataZoom: buildDataZoom({
          accent: SOLAR_BASE_COLOR,
          fillerColor: "rgba(150, 56, 0, 0.2)",
        }),
        grid: {
          left: "3%",
          right: "0.5%",
          top: "40px",
          bottom: "80px",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: chartData.map((entry) => formatDate(entry.Date)),
          name: xAxisTitle,
          nameLocation: "middle",
          nameGap: 40,
          axisLine: {
            lineStyle: {
              color: SOLAR_BASE_COLOR,
              width: 1,
            },
          },
          axisLabel: {
            color: "#666",
          },
        },
        yAxis: {
          type: "value",
          name: "Energy (kWh)",
          nameLocation: "middle",
          nameGap: 60,
          axisLine: {
            lineStyle: {
              color: "#999",
              width: 1,
            },
          },
          axisLabel: {
            color: "#666",
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: "dashed",
              color: "#eee",
            },
          },
        },
        series: [
          ...(productionTotalSeries.length > 0
            ? [
                {
                  name: PRODUCTION_TOTAL_SERIES_NAME,
                  type: "line",
                  data: productionTotalSeries,
                  itemStyle: { color: "#E6A700" },
                  lineStyle: { width: 3, color: "#E6A700" },
                  symbol: "none",
                  z: 5,
                },
              ]
            : []),
          ...productionDeviceSeries,
        ],
      };
    },
    [chartData, proddeviceNames, xAxisTitle, view],
  );

  
  
  

  // Handle date changes
  const handleDateChange = useCallback(
    (e) => {
      const value = e.target.value;
      if (view === "daily") setSelDate(value);
      else if (view === "monthly") setSelMonth(value);
      else if (view === "yearly") setSelYear(value);
    },
    [view],
  );

  if (isLoading || !dailyData.length || !hasDevices)
    return (
      <SkeletonTheme baseColor="#f8f9fa" highlightColor="#e9ecef">
        <Skeleton height={350} />
      </SkeletonTheme>
    );

  return (
    <div className="chart-container-storage">
      <h3>Production History</h3>
      <div className="controls-storage">
        <select
          onChange={(e) => setView(e.target.value)}
          value={view}
          style={{ marginRight: "10px", cursor: "pointer" }}
        >
          <option value="daily">Daily</option>
          <option value="monthly">Monthly</option>
          <option value="yearly">Yearly</option>
          <option value="all time view">All Time View</option>
        </select>

        {view === "daily" && (
          <input
            type="date"
            onChange={handleDateChange}
            value={selDate}
            min={firstKey?.day || ""}
            max={lastKey?.day || ""}
          />
        )}
        {/*  */}
        {view === "monthly" && (
          <input
            type="month"
            onChange={handleDateChange}
            value={selMonth}
            min={firstKey?.month || ""}
            max={lastKey?.month || ""}
          />
        )}

        {view === "yearly" && (
          <select onChange={handleDateChange} value={selYear}>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="chart-wrapper" style={{ height: 600 }}>
        {chartData.length > 0 ? (
          <ReactECharts
            option={echartOptions}
            style={{ width: "100%", height: "100%" }}
            opts={{ renderer: "canvas" }}
          />
        ) : (
          <div className="no-data-message">
            No data available for selected period
          </div>
        )}
      </div>
    </div>
  );
}
